import { filterRoutesByRole, flattenRoutes } from "@/utils";
import { App as AntApp, ConfigProvider } from "antd";
import { Suspense, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router";
import type { TUserRoles } from "./app/app.constants";
import AuthGuard from "./components/Guards/AuthGuard";
import ComingSoonGuard from "./components/Guards/ComingSoonGuard";
import useAppBoot from "./hooks/useAppBoot";
import useAuth from "./hooks/useAuth";
import useModuleStatus from "./hooks/useModuleStatus";
import Loader from "./pages/Loader";
import NotFound from "./pages/NotFound";
import SelectOrganization from "./pages/Auth/SelectOrganization";
import { APP_ROUTES, PUBLIC_ROUTES } from "./routes/routes";
import { antdThemeDark, antdThemeLight } from "./theme/theme";
const App = () => {
  const { role, theme } = useAuth();
  const { isEnabled } = useModuleStatus();
  const { isLoading, needsOrganization } = useAppBoot();

  const accessibleRoutes = filterRoutesByRole(
    APP_ROUTES,
    role as TUserRoles,
    isEnabled,
  );
  const flatRoutes = flattenRoutes(accessibleRoutes);
  const filteredMenu = filterRoutesByRole(
    APP_ROUTES,
    role as TUserRoles,
    isEnabled,
    true,
  );

  const navigateUrl = useMemo(() => {
    switch (role) {
      case "admin":
        return (
          filteredMenu?.[0]?.children?.[0]?.path || "/admin/employee/dashboard"
        );
      case "evaluator":
        return "/admin/recruitment/evaluation";
      case "evaluator-user":
        return "/admin/recruitment/evaluation";
      case "evaluator-manager":
        return "/admin/recruitment/evaluation";
      case "user":
        return (
          filteredMenu?.[0]?.children?.[0]?.path || "/admin/performance/kpi"
        );
      case "line-manager":
        return (
          filteredMenu?.[0]?.children?.[0]?.path || "/admin/performance/kpi"
        );
      default:
        return "/login";
    }
  }, [role, filteredMenu]);

  return (
    <ConfigProvider theme={theme === "light" ? antdThemeLight : antdThemeDark}>
      <AntApp>
        {isLoading ? (
          <Loader />
        ) : needsOrganization ? (
          // Multi-organization login without a session: only the picker is reachable.
          <Routes>
            <Route
              path="/select-organization"
              element={<SelectOrganization />}
            />
            <Route
              path="*"
              element={<Navigate to="/select-organization" replace />}
            />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<Navigate to={navigateUrl} />} />
            <Route path="/admin" element={<Navigate to={navigateUrl} />} />
            <Route path="/dashboard" element={<Navigate to={navigateUrl} />} />

            {/* Public routers */}
            {flatRoutes.map(({ path, element: Element, roles, label }, idx) => (
              <Route
                path={path}
                key={idx + path}
                element={
                  <AuthGuard roles={roles}>
                    <ComingSoonGuard
                      // isComingSoon={isComingSoon}
                      title={label}
                    >
                      {Element ? (
                        <Suspense fallback={<Loader />}>
                          <Element />
                        </Suspense>
                      ) : (
                        <NotFound />
                      )}
                    </ComingSoonGuard>
                  </AuthGuard>
                }
              />
            ))}
            {/* Public routers */}
            {PUBLIC_ROUTES.map(({ path, element: Element }, idx) => (
              <Route
                path={path}
                key={idx + path}
                element={
                  <Suspense fallback={<Loader />}>
                    <Element />
                  </Suspense>
                }
              />
            ))}
          </Routes>
        )}
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
